import { Capacitor } from "@capacitor/core";
import { type FC, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthProvider";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { biometricService } from "../../../services/biometric.service";
import { localStorageService } from "../../../services/LocalStorageService";
import { AppPlatform } from "../../../utils/models";
import { BiometricIcon } from "../icons/BiometricIcons";
import { LoginButton, LoginSeparator } from "./common";

export const getBiometricButtonText = (biometricType: string | null, platform: string): string => {
	switch (biometricType) {
		case "face":
			return platform === AppPlatform.IOS ? "Login with Face ID" : "Login with Face Recognition";
		case "fingerprint":
			return platform === AppPlatform.IOS ? "Login with Touch ID" : "Login with Fingerprint";
		default:
			return "Login with Biometrics";
	}
};

interface BiometricLoginButtonProps {
	showSeparator?: boolean;
	onAutoLoginComplete?: () => void;
	isFormLoading?: boolean;
	onBiometricLoadingChange?: (loading: boolean) => void;
}

export const BiometricLoginButton: FC<BiometricLoginButtonProps> = ({
	showSeparator = false,
	onAutoLoginComplete,
	isFormLoading = false,
	onBiometricLoadingChange,
}) => {
	const { login, isCheckingAuth, userToken } = useAuth();
	const { track, trackError } = useAnalytics();
	const { showSnackbar } = useSnackbar();
	const navigate = useNavigate();
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const hasAttemptedRef = useRef(false);

	const autoBiometricLogin = useCallback(async () => {
		if (hasAttemptedRef.current) return;
		hasAttemptedRef.current = true;
		try {
			const info = await biometricService.getPlatformInfo();

			if (!localStorageService.getSavedCredentials()) {
				localStorageService.setSavedCredentials(info.hasCredentials);
			}
			if (!localStorageService.getBiometricAvailable()) {
				localStorageService.setBiometricAvailable(info.biometricAvailable);
			}

			if (info.isNative && info.biometricAvailable && info.hasCredentials) {
				try {
					track("login", { type: "auto biometric", status: "start" });

					const credentials = await biometricService.getCredentials();

					if (credentials) {
						const result = await login(credentials.email, credentials.password);

						if (result.isSuccessful) {
							track("login", {
								type: "auto biometric",
								status: "success",
								email: credentials.email,
							});
							navigate("/", { replace: true });
						} else {
							track("login", {
								type: "auto biometric",
								status: "failed",
							});
						}
					}
				} catch (error) {
					trackError("auto biometric login error", {
						error: (error as Error).message,
					});
				}
			}
		} finally {
			// Parent must be unblocked even on error.
			onAutoLoginComplete?.();
		}
	}, [login, navigate, track, trackError, onAutoLoginComplete]);

	useEffect(() => {
		// Wait until AuthProvider finished bootstrapping, and only attempt biometric
		// login when there's no live session — otherwise we race the success path.
		if (isCheckingAuth || userToken) return;
		autoBiometricLogin();
	}, [autoBiometricLogin, isCheckingAuth, userToken]);

	const handleBiometricLogin = useCallback(async () => {
		if (isFormLoading) {
			track("biometric login blocked", {
				reason: "form submission in progress",
			});
			return;
		}

		try {
			setIsLoading(true);
			onBiometricLoadingChange?.(true);
			track("login", { type: "manual biometric", status: "start" });

			const credentials = await biometricService.getCredentials();

			if (!credentials) {
				trackError("login", {
					type: "manual biometric",
					status: "fail",
					error: "no saved credentials",
				});
				showSnackbar("No saved credentials found", "error");
				return;
			}

			const result = await login(credentials.email, credentials.password);

			if (!result.isSuccessful) {
				trackError("login", {
					type: "manual biometric",
					status: "fail",
					error: "login failed",
				});
				showSnackbar("Biometric login failed", "error");
			} else {
				track("login", {
					type: "manual biometric",
					status: "success",
					email: credentials.email,
				});
				await biometricService.updateToken(credentials.token || "");
				showSnackbar("Login successful", "success");
			}
		} catch (error) {
			trackError("login", {
				type: "manual biometric",
				status: "fail",
				error: (error as Error).message,
			});
			showSnackbar("Biometric authentication failed", "error");
		} finally {
			setIsLoading(false);
			onBiometricLoadingChange?.(false);
		}
	}, [isFormLoading, login, track, trackError, showSnackbar]);

	if (!localStorageService.getBiometricAvailable() || !localStorageService.getSavedCredentials()) {
		return null;
	}

	return (
		<>
			<LoginButton type="button" onClick={handleBiometricLogin} loading={isLoading} disabled={isLoading || isFormLoading}>
				<BiometricIcon type={localStorageService.getBiometricType()} platform={Capacitor.getPlatform()} />
				{getBiometricButtonText(localStorageService.getBiometricType(), Capacitor.getPlatform())}
			</LoginButton>
			{showSeparator && <LoginSeparator sx={{ my: "1rem" }}>Or continue with</LoginSeparator>}
		</>
	);
};
